"use strict";

/* ------------------------------------------------------------------ *
 * Mapper 119: TQROM (MMC3 with CHR ROM + CHR RAM)
 * ------------------------------------------------------------------ *
 * A standard MMC3 except that bit 6 of the CHR bank register values
 * selects between CHR ROM (0) and CHR RAM (1). Bits 0-5 specify the
 * bank within the selected chip, allowing up to 64 KB CHR ROM and
 * 8 KB CHR RAM.
 *
 * A 74HC32 ORs PPU A13 with CHR A16 (bit 6) to generate the ROM
 * chip-enable, while CHR A16 directly enables the RAM chip.
 * Used by Pin-Bot and High Speed (both by Rare).
 * See https://www.nesdev.org/wiki/INES_Mapper_119
 */

function register(Mappers) {
  var utils = require("../utils");
  var Tile = require("../tile");

  // Pre-initialise the MMC3 register file per instance so two machines
  // running at once do not share bankSelect, irqCounter, etc. The shared
  // prototype still carries the CMD_* constants and method definitions.
  Mappers[119] = function (nes) {
    // The MMC3 constructor initialises command, bankSelect, irq state, etc.
    Mappers[4].call(this, nes);

    // 8 KB of CHR RAM (8 x 1 KB banks).
    this.chrRam = new Uint8Array(8192);

    // Pre-decoded tiles for CHR RAM banks. Each 1 KB bank has 64 tiles
    // (1 KB / 16 bytes per tile). These are persistent Tile objects: when
    // a CHR RAM bank is loaded into a PPU slot, ptTile entries point here,
    // and PPU patternWrite() updates them in place on $2007 writes.
    this.chrRamTiles = new Array(8);
    for (var i = 0; i < 8; i++) {
      this.chrRamTiles[i] = new Array(64);
      for (var j = 0; j < 64; j++) {
        this.chrRamTiles[i][j] = new Tile();
      }
    }

    // Tracks which CHR RAM bank (0-7) is mapped at each 1 KB PPU pattern
    // table slot (0-7 for addresses $0000-$1FFF), or -1 if CHR ROM is there.
    this.chrRamSlots = [-1, -1, -1, -1, -1, -1, -1, -1];
  };

  // Inherit from MMC3 (which inherits from NROM).
  Mappers[119].prototype = new Mappers[4]();

  // TQROM uses standard MMC3 mirroring via $A000.
  Mappers[119].prototype.ownsMirroring = false;

  // Load a 1 KB CHR ROM bank into a PPU slot, clearing the slot map.
  Mappers[119].prototype.load1kChrRomBank = function (bank, address) {
    this.saveChrRamSlot(address);
    this.chrRamSlots[address >> 10] = -1;
    this.load1kVromBank(bank & 0x3f, address);
  };

  // Load a 1 KB CHR RAM bank into a PPU slot, preserving the slot map.
  Mappers[119].prototype.load1kChrRamBank = function (bank, address) {
    this.nes.ppu.triggerRendering();
    bank &= 0x07;

    // Save the old CHR RAM content if this slot had a different bank mapped.
    this.saveChrRamSlot(address);

    var slot = address >> 10;
    this.chrRamSlots[slot] = bank;

    // Copy CHR RAM data into PPU VRAM.
    var srcOffset = bank * 1024;
    utils.copyArrayElements(
      this.chrRam,
      srcOffset,
      this.nes.ppu.vramMem,
      address,
      1024,
    );

    // Rebuild tiles from CHR RAM data and install them in ptTile.
    this.rebuildChrRamTiles(bank);
    var baseIndex = address >> 4;
    for (var i = 0; i < 64; i++) {
      this.nes.ppu.ptTile[baseIndex + i] = this.chrRamTiles[bank][i];
    }
  };

  // Load a 2 KB CHR window: either two 1 KB CHR RAM banks (2 KB aligned
  // inside the 8 KB RAM) or two 1 KB CHR ROM banks.
  Mappers[119].prototype.load2kTqromBank = function (arg, address) {
    if (arg & 0x40) {
      // CHR RAM: 8 KB in 2 KB pages, so the bottom bit of the pair index
      // goes too. The register value uses 1 KB units; for 2 KB we take
      // bits 2-3 (mask 0x06) which gives a 2 KB aligned index 0,2,4,6.
      var ramBank = arg & 0x06;
      this.load1kChrRamBank(ramBank, address);
      this.load1kChrRamBank(ramBank + 1, address + 0x0400);
    } else {
      // CHR ROM: the MMC3 ignores bit 0 of the 2 KB register, so the pair
      // is (arg & 0x3e) and (arg & 0x3e) + 1. Using the same helper keeps
      // the CHR ROM logic identical to MMC3 (including the bit-0-ignore
      // behaviour games rely on).
      var romBank = arg & 0x3e;
      this.load1kChrRomBank(romBank, address);
      this.load1kChrRomBank(romBank + 1, address + 0x0400);
    }
  };

  // Load a 1 KB CHR window: either CHR RAM or CHR ROM depending on bit 6.
  Mappers[119].prototype.load1kTqromBank = function (arg, address) {
    if (arg & 0x40) {
      this.load1kChrRamBank(arg & 0x07, address);
    } else {
      this.load1kChrRomBank(arg, address);
    }
  };

  // Re-derives all eight CHR windows from R0-R5 and the CHR map mode,
  // but with TQROM's bit-6 chip select. This mirrors the MMC3's
  // updateChrBanks() layout, only the helpers differ.
  Mappers[119].prototype.updateChrBanks = function () {
    var s = this.bankSelect;

    if (this.chrAddressSelect === 0) {
      // Mode 0: two 2 KB at $0000/$0800, four 1 KB at $1000-$1C00.
      this.load2kTqromBank(s[0], 0x0000);
      this.load2kTqromBank(s[1], 0x0800);
      this.load1kTqromBank(s[2], 0x1000);
      this.load1kTqromBank(s[3], 0x1400);
      this.load1kTqromBank(s[4], 0x1800);
      this.load1kTqromBank(s[5], 0x1c00);
    } else {
      // Mode 1: four 1 KB at $0000-$0C00, two 2 KB at $1000/$1800.
      this.load1kTqromBank(s[2], 0x0000);
      this.load1kTqromBank(s[3], 0x0400);
      this.load1kTqromBank(s[4], 0x0800);
      this.load1kTqromBank(s[5], 0x0c00);
      this.load2kTqromBank(s[0], 0x1000);
      this.load2kTqromBank(s[1], 0x1800);
    }
  };

  // CHR commands (0-5): the $8001 write has already updated bankSelect,
  // so re-deriving every window keeps the CHR RAM slot map in step with
  // the registers. PRG commands (6/7) pass through to MMC3's handler
  // which calls updatePrgBanks() directly.
  Mappers[119].prototype.executeCommand = function (cmd, arg) {
    if (cmd < this.CMD_SEL_ROM_PAGE1) {
      this.updateChrBanks();
    } else {
      Mappers[4].prototype.executeCommand.apply(this, arguments);
    }
  };

  // Save the current vramMem content of a 1 KB PPU slot back to chrRam.
  // This must be called before overwriting a slot that has CHR RAM mapped,
  // so that any PPU $2007 writes to that region are preserved.
  Mappers[119].prototype.saveChrRamSlot = function (address) {
    var slot = address >> 10;
    var bank = this.chrRamSlots[slot];
    if (bank === -1) return;
    utils.copyArrayElements(
      this.nes.ppu.vramMem,
      slot << 10,
      this.chrRam,
      bank * 1024,
      1024,
    );
  };

  // Rebuild the pre-decoded Tile objects for a CHR RAM bank from raw bytes.
  Mappers[119].prototype.rebuildChrRamTiles = function (bank) {
    var base = bank * 1024;
    for (var i = 0; i < 1024; i++) {
      var tileIndex = i >> 4;
      var leftOver = i % 16;
      if (leftOver < 8) {
        this.chrRamTiles[bank][tileIndex].setScanline(
          leftOver,
          this.chrRam[base + i],
          this.chrRam[base + i + 8],
        );
      } else {
        this.chrRamTiles[bank][tileIndex].setScanline(
          leftOver - 8,
          this.chrRam[base + i - 8],
          this.chrRam[base + i],
        );
      }
    }
  };

  // Allow PPU writes to pattern table addresses that are mapped to CHR RAM.
  Mappers[119].prototype.canWriteChr = function (address) {
    if (address >= 0x2000) return false;
    return this.chrRamSlots[address >> 10] !== -1;
  };

  // Soft reset: the PPU's pattern memory is wiped by nes.reset() before
  // mmap.loadROM() is called, so the old slot map no longer describes
  // anything real. Clearing it keeps saveChrRamSlot() from flushing that
  // blank VRAM over the CHR RAM buffer, which survives the reset like it
  // does on hardware.
  Mappers[119].prototype.reset = function () {
    Mappers[4].prototype.reset.apply(this);
    this.chrRamSlots = [-1, -1, -1, -1, -1, -1, -1, -1];
  };

  // Power-on: run MMC3's loadROM (which calls reset() and loadCHRROM()),
  // then re-derive CHR windows from the power-on registers so the slot
  // map agrees with what the mapper actually maps. This also restores
  // any CHR RAM content into windows that the registers select.
  Mappers[119].prototype.loadROM = function () {
    Mappers[4].prototype.loadROM.apply(this);
    this.updateChrBanks();
  };

  Mappers[119].prototype.toJSON = function () {
    // Flush any pending CHR RAM writes from vramMem back to chrRam.
    for (var slot = 0; slot < 8; slot++) {
      this.saveChrRamSlot(slot << 10);
    }
    var s = Mappers[4].prototype.toJSON.apply(this);
    s.chrRam = Array.from(this.chrRam);
    s.chrRamSlots = this.chrRamSlots.slice();
    return s;
  };

  // fromJSON order: restore chrRam, clear slots, let MMC3.fromJSON re-derive
  // windows (which calls updateChrBanks via its own fromJSON), then the
  // PPU restore will fill the tile objects we just installed.
  Mappers[119].prototype.fromJSON = function (s) {
    this.chrRam = new Uint8Array(s.chrRam);
    this.chrRamSlots = [-1, -1, -1, -1, -1, -1, -1, -1];
    Mappers[4].prototype.fromJSON.apply(this, arguments);
  };
}

module.exports = { register: register };
